import React from 'react';
import { Search, X } from 'lucide-react';
import { TYPE_OPTIONS, RANGE_OPTIONS, SORT_OPTIONS, activePills, clearPill, clearAll, stateOptions } from '../../../lib/numberFilters.js';

const SELECT = 'rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-white focus:border-civix-purple/60 focus:outline-hidden';

/**
 * Search / filter / sort toolbar for the Active Numbers list, with removable pills for applied filters.
 * @param {{numbers: object[], filters: object, onChange: (f: object) => void, shown: number}} props
 *   `numbers` is the full inventory (the state dropdown is built from it); `shown` is the visible count.
 */
export default function NumberFilterBar({ numbers, filters, onChange, shown }) {
  const set = (patch) => onChange({ ...filters, ...patch });
  const { list: states, unknown } = stateOptions(numbers);
  const pills = activePills(filters);

  return (
    <div className="space-y-2" data-testid="number-filter-bar">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
          <input
            value={filters.search}
            onChange={(e) => set({ search: e.target.value })}
            placeholder="Search number, area code, city or state"
            aria-label="Search numbers"
            data-testid="number-search"
            className={`${SELECT} w-full pl-8 pr-8`}
          />
          {filters.search && (
            <button type="button" onClick={() => set({ search: '' })} aria-label="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"><X className="h-3.5 w-3.5" /></button>
          )}
        </label>

        <select aria-label="Filter by state" value={filters.state} onChange={(e) => set({ state: e.target.value })} data-testid="number-state-filter" className={SELECT}>
          <option value="all" className="text-black">All States ({numbers.length})</option>
          {states.map((s) => <option key={s.code} value={s.code} className="text-black">{s.code} - {s.name} ({s.count})</option>)}
          {unknown > 0 && <option value="none" className="text-black">Unknown state ({unknown})</option>}
        </select>

        <select aria-label="Filter by type" value={filters.type} onChange={(e) => set({ type: e.target.value })} data-testid="number-type-filter" className={SELECT}>
          {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value} className="text-black">{o.label}</option>)}
        </select>

        <select aria-label="Filter by date added" value={filters.range} onChange={(e) => set({ range: e.target.value, ...(e.target.value !== 'custom' ? { from: '', to: '' } : {}) })} data-testid="number-range-filter" className={SELECT}>
          {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value} className="text-black">{o.label}</option>)}
        </select>

        <select aria-label="Sort numbers" value={filters.sort} onChange={(e) => set({ sort: e.target.value })} data-testid="number-sort" className={SELECT}>
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value} className="text-black">{o.label}</option>)}
        </select>
      </div>

      {filters.range === 'custom' && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-white/60" data-testid="number-custom-range">
          <span>Added from</span>
          <input type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => set({ from: e.target.value })} aria-label="From date" className={SELECT} />
          <span>to</span>
          <input type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => set({ to: e.target.value })} aria-label="To date" className={SELECT} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-white/55" data-testid="number-count">Showing {shown} of {numbers.length} number{numbers.length === 1 ? '' : 's'}</span>
        {pills.map((p) => (
          <span key={p.key} className="inline-flex items-center gap-1 rounded-full border border-civix-purple/30 bg-civix-purple/15 py-0.5 pl-2.5 pr-1 text-civix-purpleSoft" data-testid={`pill-${p.key}`}>
            {p.label}
            <button type="button" onClick={() => onChange(clearPill(filters, p.key))} aria-label={`Remove filter ${p.label}`} className="rounded-full p-0.5 hover:bg-white/10"><X className="h-3 w-3" /></button>
          </span>
        ))}
        {pills.length > 0 && <button type="button" onClick={() => onChange(clearAll(filters))} className="text-white/50 underline hover:text-white">Clear all</button>}
      </div>
    </div>
  );
}
